import { ComponentIds, Entity, HierarchyUtils, ProductComponent } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import {
  CoreApi,
  Dimension,
  ImageType,
  Product,
  ProductImageDetails,
  SlidingDoorComponent,
  Wall
} from '@inter-ikea-storageone/range';
import * as R from 'ramda';
import { ProductType } from '@inter-ikea-storageone/range';
import CustomSlidingDoorIcon from './icons/customSlidingDoor.jpg';
import { CustomizedSlidingDoorComponent } from '@inter-ikea-storageone/range-pax';

export type RoomCorners = 'front-left' | 'front-right' | 'back-left' | 'back-right';

export const getMeasurementsForWall = (coreApi: CoreApi, selected: Entity) => (wall: Wall) => {
  // If one of the frames had interiors on the front wall we filter those entities
  // that are part of the corner solution
  const filterCornerSolutionEntities = (entity: Entity) => {
    const cornerSolutionEntityIds = coreApi.entities.findAllRelatedEntityIds([selected]);
    return !cornerSolutionEntityIds.find(entityId => entityId === entity.id);
  };

  const entities = coreApi.entities
    .getAll()
    .filter(
      entity =>
        coreApi.entities.getEntityWall(entity) === wall &&
        entity !== selected &&
        !entity.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
    )
    .filter(filterCornerSolutionEntities);
  return coreApi.entities.getMeasurements(entities);
};

export const getCornerSolutionPlacement = (
  coreApi: CoreApi,
  entity: Entity,
  orientations: { left: string; right: string }
): RoomCorners => {
  const productId = entity.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
    ?.productRef;
  const wall = coreApi.entities.getEntityWall(entity);
  const isLeftOrientation = productId === orientations.left;
  if ((wall === 'front' && isLeftOrientation) || (wall === 'left' && !isLeftOrientation)) {
    return 'front-left';
  } else if ((wall === 'front' && !isLeftOrientation) || (wall === 'right' && isLeftOrientation)) {
    return 'front-right';
  } else if ((wall === 'back' && !isLeftOrientation) || (wall === 'left' && isLeftOrientation)) {
    return 'back-left';
  }
  return 'back-right';
};

const isDrawerExcludeLockable = (coreApi: CoreApi, entity: Entity): boolean => {
  const productType = coreApi.entities.getProductType(entity);
  return productType
    ? ([
        ProductType.PAX_DRAWER,
        ProductType.PAX_DRAWER_FRAMED,
        ProductType.PAX_DRAWER_FRAMED_GLASS,
        ProductType.PAX_GLASS_DRAWER
      ] as string[]).includes(productType)
    : false;
};

export const getIsDrawer = (coreApi: CoreApi, selectedEntityId: number) => {
  const drawerEntity = coreApi.entities.find(selectedEntityId);
  if (!drawerEntity) {
    return false;
  }
  return isDrawerExcludeLockable(coreApi, drawerEntity);
};

export const getIsOuterLight = (coreApi: CoreApi, selectedEntityId: number) => {
  const lightEntity = coreApi.entities.find(selectedEntityId);
  if (!lightEntity) {
    return false;
  }
  return coreApi.entities.isProductType(lightEntity, ProductType.PAX_OUTER_LIGHT);
};

export const getIsInnerLight = (coreApi: CoreApi, selectedEntityId: number) => {
  const lightEntity = coreApi.entities.find(selectedEntityId);
  if (!lightEntity) {
    return false;
  }
  return (
    coreApi.entities.isProductType(lightEntity, ProductType.PAX_INNER_LIGHT) ||
    coreApi.entities.isProductType(lightEntity, ProductType.PAX_INNER_LIGHT_OVERSIDAN)
  );
};

export const getIsShelf = (coreApi: CoreApi, selectedEntityId: number) => {
  const entity = coreApi.entities.find(selectedEntityId);
  if (!entity) {
    return false;
  }
  return (
    coreApi.entities.isProductType(entity, ProductType.PAX_SHELF) ||
    coreApi.entities.isProductType(entity, ProductType.PAX_GLASS_SHELF)
  );
};

export const getIsDivider = (coreApi: CoreApi, selectedEntityId: number) => {
  const entity = coreApi.entities.find(selectedEntityId);
  if (!entity) {
    return false;
  }
  return coreApi.entities.isOneOfProductTypes(entity, [
    ProductType.PAX_L_DIVIDER,
    ProductType.PAX_T_DIVIDER
  ]);
};

export const getIs580ClothRail = (coreApi: CoreApi, selectedEntityId: number) => {
  const entity = coreApi.entities.find(selectedEntityId);
  if (!entity) {
    return false;
  }
  return coreApi.entities.isProductType(entity, ProductType.PAX_CLOTH_RAIL_580);
};

export const getIsCornerSolution = (coreApi?: CoreApi, selectedEntityId?: number) => {
  if (coreApi && selectedEntityId) {
    const frameEntity = coreApi.entities.find(selectedEntityId);
    if (frameEntity) {
      return coreApi.entities.isProductType(frameEntity, ProductType.PAX_CORNER_SOLUTION_ASM);
    }
  }
  return false;
};

type FilterOption = {
  [dimension: number]: string;
};

export const constructDimensionOptions = (dimension: Dimension) =>
  R.reduce((acc, r: Product) => {
    return {
      ...acc,
      [r.dimensions[dimension]]: r.dimensions[dimension]
    };
  }, {});

export const getDimensionOptions = (dimension: Dimension) =>
  R.pipe<Product[], FilterOption>(constructDimensionOptions(dimension));

export const shouldDisableSlidingDoorProducts = (coreApi: CoreApi) => {
  const SLIDING_DOOR_HEIGHT_MARGIN = 40;
  const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const ceilingHeight = coreApi.kvadratRoom?.getSize().Y;
  if (frames.length === 0 || !ceilingHeight) {
    return false;
  }
  const maxFrameHeight = getMaximalFrameHeight(frames);
  return ceilingHeight < maxFrameHeight + SLIDING_DOOR_HEIGHT_MARGIN;
};

export const getMaximalFrameHeight = (frames: Entity[]) => {
  const getFrameHeight = (frame: Entity) => {
    return (
      frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size.height || 0
    );
  };
  return Math.max(...frames.map(frame => getFrameHeight(frame)));
};

export const getFrameSlidingDoor = (frame: Entity): Entity | null => {
  const automaticGroup = HierarchyUtils.findWithComponentInUpwardsHierarchy(
    frame,
    ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID
  );
  const slidingDoor = automaticGroup
    ? HierarchyUtils.children(automaticGroup).filter(child =>
        child.hasComponent(SlidingDoorComponent.id)
      )
    : [];

  return slidingDoor.length > 0 ? slidingDoor[0] : null;
};

export const getCustomSlidingDoorImage = (): ProductImageDetails => {
  return {
    id: '1',
    sources: [{ src: CustomSlidingDoorIcon, width: 250, height: 250 }],
    type: ImageType.MPP
  };
};

export const notCustomised = (product: Product, coreApi: CoreApi) => {
  const entitiesOfProductInScene = coreApi.entities
    .getAll()
    .filter(e => coreApi.entities.getProductRef(e) === product.coreProductId);
  return (
    entitiesOfProductInScene.length > 0 &&
    !entitiesOfProductInScene.every(e => e.hasComponent(CustomizedSlidingDoorComponent.id))
  );
};

export const selectedDoorNotCustomised = (product: Product, coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity || selectedEntity?.hasComponent(CustomizedSlidingDoorComponent.id)) {
    return false;
  }
  return coreApi.entities.getProductRef(selectedEntity) === product.coreProductId;
};
